import React from 'react';
import TextField from '@material-ui/core/TextField';
import { MuiPickersUtilsProvider, KeyboardTimePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import jaLocale from 'date-fns/locale/ja';
import format from 'date-fns/format';
import isValid from 'date-fns/isValid';

class ExtendedUtils extends DateFnsUtils {}

export default function TimePicker(props) {
  const {
    name,
    value,
    onChange,
    error = false,
    minTime,
    maxTime,
    label = '',
    disabled = false,
    helperText = '',
  } = props;

  const isTimeString = (str) => {
    if (typeof str !== 'string') return false;
    return /^(([01]\d|2[0-3]):([0-5]\d)(:[0-5]\d)?|24:00(:00)?)$/.test(str);
  };

  const normalizeTime = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') return '';

    const parts = timeStr.split(':');
    if (parts.length < 2) return '';

    const hour = String(parts[0]).padStart(2, '0');
    const minute = String(parts[1]).padStart(2, '0');

    if (hour === '24' && minute === '00') {
      return '24:00';
    }

    const hourNum = Number(hour);
    const minuteNum = Number(minute);

    if (
      Number.isNaN(hourNum)
      || Number.isNaN(minuteNum)
      || hourNum < 0
      || hourNum > 23
      || minuteNum < 0
      || minuteNum > 59
    ) {
      return '';
    }

    return `${hour}:${minute}`;
  };

  const normalizeDisplayTime = (timeStr) => {
    const normalized = normalizeTime(timeStr);
    if (!normalized) return '';
    return normalized === '00:00' ? '24:00' : normalized;
  };

  const stringToDate = (timeStr) => {
    if (!isTimeString(timeStr)) return null;

    const normalized = normalizeTime(timeStr);
    if (!normalized) return null;

    const date = new Date();

    if (normalized === '24:00') {
      date.setHours(0, 0, 0, 0);
      return date;
    }

    const [h, m] = normalized.split(':').map(Number);
    date.setHours(h, m, 0, 0);
    return date;
  };

  const dateToString = (date) => {
    if (!date || !isValid(date)) return '';

    const hhmm = format(date, 'HH:mm');
    return hhmm === '00:00' ? '24:00' : hhmm;
  };

  const emitChange = (nextValue) => {
    if (!onChange) return;

    onChange({
      target: {
        name,
        value: nextValue,
      },
    });
  };

  const handleTimeChange = (date, keyboardInputValue) => {
    if (keyboardInputValue === '') {
      emitChange('');
      return;
    }

    if (typeof keyboardInputValue === 'string') {
      if (isTimeString(keyboardInputValue)) {
        emitChange(normalizeTime(keyboardInputValue));
      }
      return;
    }

    const formatted = dateToString(date);
    if (formatted !== '') {
      emitChange(formatted);
    }
  };

  const displayValue = normalizeDisplayTime(value);

  return (
    <MuiPickersUtilsProvider utils={ExtendedUtils} locale={jaLocale}>
      <KeyboardTimePicker
        label={label}
        value={stringToDate(value)}
        onChange={handleTimeChange}
        format="HH:mm"
        ampm={false}
        inputVariant="outlined"
        variant="inline"
        autoComplete="off"
        disabled={disabled}
        error={error}
        helperText={helperText}
        minutesStep={5}
        minTime={minTime}
        maxTime={maxTime}
        cancelLabel="キャンセル"
        okLabel="設定"
        clearLabel="クリア"
        invalidDateMessage="時間の形式が正しくありません"
        KeyboardButtonProps={{
          'aria-label': 'change time',
        }}
        clearable
        autoOk
        TextFieldComponent={(textFieldProps) => (
          <TextField
            {...textFieldProps}
            label={label}
            value={displayValue}
            error={error}
            helperText={helperText}
            disabled={disabled}
            autoComplete="off"
            onChange={(e) => {
              const inputValue = e.target.value;

              if (inputValue === '') {
                emitChange('');
                return;
              }

              // 24:00 を含む完成値のみ親へ反映
              if (isTimeString(inputValue)) {
                emitChange(normalizeTime(inputValue));
              }

              // 入力途中は pickers 側にも流す
              if (textFieldProps.onChange) {
                textFieldProps.onChange(e);
              }
            }}
          />
        )}
      />
    </MuiPickersUtilsProvider>
  );
}
